import React, { useState, useEffect } from 'react';
import { FaPlus, FaDownload, FaTrashAlt, FaToggleOn, FaToggleOff, FaUserTie, FaPrint } from 'react-icons/fa';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';

const QRCodeManagement = () => {
  const { user } = useAuth();
  const [tables, setTables] = useState([]);
  const [captains, setCaptains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);
  const [selectedCaptainId, setSelectedCaptainId] = useState('');
  const [selectedCaptainIds, setSelectedCaptainIds] = useState([]);
  const [numberOfTables, setNumberOfTables] = useState(1);
  const [specificTableNumber, setSpecificTableNumber] = useState('');
  const [fromTableNumber, setFromTableNumber] = useState('');
  const [toTableNumber, setToTableNumber] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Get restaurant name from user data or use default
  const restaurantName = user?.restaurantName || user?.businessName || user?.name || 'Restaurant';

  useEffect(() => {
    fetchTables();
    fetchCaptains();
  }, []);

  const fetchTables = async () => {
    try {
      const response = await api.get('/vendor/tables');
      setTables(response.data.data || []);
    } catch (error) {
      toast.error('Failed to fetch tables');
    } finally {
      setLoading(false);
    }
  };

  const fetchCaptains = async () => {
    try {
      const response = await api.get('/vendor/captains');
      setCaptains(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch captains');
    }
  };

  const openAssignModal = (table) => {
    setSelectedTable(table);
    setSelectedCaptainId(table.assignedCaptain?._id || '');
    setSelectedCaptainIds(
      Array.isArray(table.assignedCaptain)
        ? table.assignedCaptain.map(c => c._id)
        : []
    );
    setShowAssignModal(true);
  };

  const assignCaptain = async () => {
    if (!selectedTable) return;
    
    try {
      await api.put(`/vendor/tables/${selectedTable._id}/assign-captain`, {
        captainIds: selectedCaptainIds
      });
      toast.success('Captains updated successfully');
      setShowAssignModal(false);
      fetchTables();
    } catch (error) {
      toast.error('Failed to assign captain');
    }
  };

  const generateMultipleTables = async (e) => {
    e.preventDefault();
    try {
      await api.post('/vendor/tables/generate', { numberOfTables });
      toast.success(`${numberOfTables} QR codes generated successfully`);
      setShowModal(false);
      setNumberOfTables(1);
      fetchTables();
    } catch (error) {
      toast.error('Failed to generate QR codes');
    }
  };

  const generateSpecificTable = async (e) => {
    e.preventDefault();
    try {
      // Validation: Either specificTableNumber should be provided OR both fromTableNumber and toTableNumber
      const hasSpecificTable = specificTableNumber.trim();
      const hasFromTo = fromTableNumber.trim() && toTableNumber.trim();
      
      if (!hasSpecificTable && !hasFromTo) {
        toast.error('Either provide a Table Number or both From and To table numbers');
        return;
      }
      
      if (hasSpecificTable && hasFromTo) {
        toast.error('Provide either a single Table Number OR From/To range, not both');
        return;
      }
      
      if (fromTableNumber && toTableNumber) {
        const fromNum = parseInt(fromTableNumber);
        const toNum = parseInt(toTableNumber);
        
        if (fromNum > toNum) {
          toast.error('From table number cannot be greater than to table number');
          return;
        }
        
        // Create multiple QR codes in a loop
        for (let i = fromNum; i <= toNum; i++) {
          try {
            await api.post('/vendor/tables/generate-specific', { tableNumber: i });
          } catch (error) {
            // If table already exists, show the specific error and continue
            if (error.response?.data?.message === "Table already exists") {
              toast.error(`Table ${i} already exists`);
              continue;
            }
            // For other errors, stop the process
            throw error;
          }
        }
        
        toast.success(`Generated QR codes successfully`);
      } else {
        // Single table generation
        await api.post('/vendor/tables/generate-specific', { tableNumber: parseInt(specificTableNumber) });
        toast.success('QR code generated successfully');
      }
      
      // Reset form and close popup
      setSpecificTableNumber('');
      setFromTableNumber('');
      setToTableNumber('');
      setShowModal(false);
      fetchTables();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to generate QR code';
      toast.error(errorMessage);
    }
  };

  const downloadQRCode = async (table, showToast = true) => {
    try {
      // Create a canvas to compose the image with text
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Set canvas size (larger for better quality)
      canvas.width = 600;
      canvas.height = 700;
      
      // Fill background with white
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Add border
      ctx.strokeStyle = '#ccc';
      ctx.lineWidth = 4;
      ctx.strokeRect(20, 20, canvas.width - 40, canvas.height - 40);
      
      // Add restaurant name
      ctx.fillStyle = '#2c3e50';
      ctx.font = 'bold 48px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(restaurantName, canvas.width / 2, 80);
      
      // Add table number
      ctx.fillStyle = '#333';
      ctx.font = 'bold 64px Arial';
      ctx.fillText(`Table ${table.tableNumber}`, canvas.width / 2, 160);
      
      // Load and draw QR code
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        // Calculate QR code size and position (more space since no instructions)
        const qrSize = Math.min(canvas.width - 100, canvas.height - 250);
        const qrX = (canvas.width - qrSize) / 2;
        const qrY = 200;
        
        // Draw QR code with padding
        ctx.fillStyle = 'white';
        ctx.fillRect(qrX - 25, qrY - 25, qrSize + 50, qrSize + 50);
        ctx.drawImage(img, qrX, qrY, qrSize, qrSize);
        
        // Convert canvas to blob and download
        canvas.toBlob((blob) => {
          const blobUrl = window.URL.createObjectURL(blob);
          
          // Create and trigger download
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = `Table-${table.tableNumber}-QR.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          // Clean up the blob URL
          window.URL.revokeObjectURL(blobUrl);
          
          if (showToast) {
            toast.success('QR code downloaded');
          }
        }, 'image/png');
      };
      
      img.onerror = async () => {
        // Fallback to original QR code if image loading fails
        try {
          const response = await fetch(table.qrCode);
          const blob = await response.blob();
          const blobUrl = window.URL.createObjectURL(blob);
          
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = `Table-${table.tableNumber}-QR.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          window.URL.revokeObjectURL(blobUrl);
          
          if (showToast) {
            toast.success('QR code downloaded');
          }
        } catch (fallbackError) {
          console.error('Fallback download error:', fallbackError);
          if (showToast) {
            toast.error('Failed to download QR code');
          }
        }
      };
      
      img.src = table.qrCode;
      
    } catch (error) {
      console.error('Download error:', error);
      if (showToast) {
        toast.error('Failed to download QR code');
      }
    }
  };

  const downloadAllQRCodes = () => {
    if (tables.length === 0) {
      toast.error('No QR codes to download');
      return;
    }
    tables.forEach((table, index) => {
      setTimeout(() => {
        downloadQRCode(table, false);
      }, index * 500);
    });
    toast.success(`Downloading ${tables.length} QR codes...`);
  };

  const printQRCode = (table) => {
    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    
    // Generate HTML content for printing (same as bulk print layout)
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Table ${table.tableNumber} QR Code</title>
          <style>
            body {
              margin: 0;
              padding: 0;
              font-family: Arial, sans-serif;
              background: white;
            }
            
            .qr-page {
              width: 100vw;
              height: 100vh;
              display: flex;
              align-items: center;
              justify-content: center;
              page-break-after: always;
            }
            
            .qr-container {
              text-align: center;
              width: 100%;
              height: 100%;
              display: flex;
              flex-direction: column;
              justify-content: space-between;
              padding: 20px;
              box-sizing: border-box;
            }
            
            .restaurant-name {
              font-size: 48px;
              font-weight: bold;
              color: #2c3e50;
              border-bottom: 3px solid #3498db;
              padding-bottom: 15px;
              margin-bottom: 20px;
            }
            
            .qr-title {
              font-size: 64px;
              font-weight: bold;
              color: #333;
              margin-bottom: 30px;
            }
            
            .qr-code-wrapper {
              flex: 1;
              display: flex;
              align-items: center;
              justify-content: center;
              min-height: 400px;
            }
            
            .qr-image {
              max-width: min(90vw, 90vh);
              max-height: min(90vw, 90vh);
              width: auto;
              height: auto;
              border: 4px solid #ccc;
              border-radius: 15px;
              padding: 25px;
              background: white;
              box-shadow: 0 6px 30px rgba(0,0,0,0.15);
            }
            
            .qr-instructions {
              font-size: 24px;
              color: #666;
              line-height: 1.6;
              margin-top: 30px;
            }
            
            @media print {
              body { 
                margin: 0;
                padding: 0;
              }
              
              .qr-page {
                width: 100%;
                height: 100vh;
                page-break-after: always;
              }
              
              .qr-container {
                padding: 15px;
              }
              
              .restaurant-name {
                font-size: 36px;
                border-bottom: 2px solid #3498db;
                padding-bottom: 10px;
                margin-bottom: 15px;
              }
              
              .qr-title {
                font-size: 48px;
                margin-bottom: 20px;
              }
              
              .qr-code-wrapper {
                min-height: 500px;
              }
              
              .qr-image {
                max-width: min(85vw, 85vh);
                max-height: min(85vw, 85vh);
                border: 3px solid #ccc;
                padding: 20px;
              }
              
              .qr-instructions {
                font-size: 18px;
                margin-top: 20px;
              }
            }
          </style>
        </head>
        <body>
          <div class="qr-page">
            <div class="qr-container">
              <div class="restaurant-name">${restaurantName}</div>
              <div class="qr-title">Table ${table.tableNumber}</div>
              <div class="qr-code-wrapper">
                <img src="${table.qrCode}" alt="Table ${table.tableNumber} QR Code" class="qr-image" />
              </div>
              <div class="qr-instructions">
                <strong>How to order:</strong><br>
                1. Scan this QR code with your phone<br>
                2. Browse the menu<br>
                3. Place your order<br>
                4. Enjoy your meal!
              </div>
            </div>
          </div>
          <script>
            window.onload = function() {
              setTimeout(() => {
                window.print();
                window.close();
              }, 500);
            };
          </script>
        </body>
      </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
    
    toast.success('Print dialog opened for Table ' + table.tableNumber);
  };

  const printAllQRCodes = () => {
    if (tables.length === 0) {
      toast.error('No QR codes to print');
      return;
    }
    
    // Create a new window for printing all QR codes
    const printWindow = window.open('', '_blank');
    
    // Generate HTML content for all QR codes with full page QR codes
    const qrCodesHTML = tables.map((table, index) => `
      <div class="qr-page" ${index > 0 ? 'style="page-break-before: always;"' : ''}>
        <div class="qr-container">
          <div class="restaurant-name">${restaurantName}</div>
          <div class="qr-title">Table ${table.tableNumber}</div>
          <div class="qr-code-wrapper">
            <img src="${table.qrCode}" alt="Table ${table.tableNumber} QR Code" class="qr-image" />
          </div>
          <div class="qr-instructions">
            <strong>How to order:</strong><br>
            1. Scan this QR code with your phone<br>
            2. Browse the menu<br>
            3. Place your order<br>
            4. Enjoy your meal!
          </div>
        </div>
      </div>
    `).join('');
    
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>All QR Codes</title>
          <style>
            body {
              margin: 0;
              padding: 0;
              font-family: Arial, sans-serif;
              background: white;
            }
            
            .qr-page {
              width: 100vw;
              height: 100vh;
              display: flex;
              align-items: center;
              justify-content: center;
              page-break-after: always;
            }
            
            .qr-container {
              text-align: center;
              width: 100%;
              height: 100%;
              display: flex;
              flex-direction: column;
              justify-content: space-between;
              padding: 20px;
              box-sizing: border-box;
            }
            
            .restaurant-name {
              font-size: 48px;
              font-weight: bold;
              color: #2c3e50;
              border-bottom: 3px solid #3498db;
              padding-bottom: 15px;
              margin-bottom: 20px;
            }
            
            .qr-title {
              font-size: 64px;
              font-weight: bold;
              color: #333;
              margin-bottom: 30px;
            }
            
            .qr-code-wrapper {
              flex: 1;
              display: flex;
              align-items: center;
              justify-content: center;
              min-height: 400px;
            }
            
            .qr-image {
              max-width: min(90vw, 90vh);
              max-height: min(90vw, 90vh);
              width: auto;
              height: auto;
              border: 4px solid #ccc;
              border-radius: 15px;
              padding: 25px;
              background: white;
              box-shadow: 0 6px 30px rgba(0,0,0,0.15);
            }
            
            .qr-instructions {
              font-size: 24px;
              color: #666;
              line-height: 1.6;
              margin-top: 30px;
            }
            
            @media print {
              body { 
                margin: 0;
                padding: 0;
              }
              
              .qr-page {
                width: 100%;
                height: 100vh;
                page-break-after: always;
              }
              
              .qr-container {
                padding: 15px;
              }
              
              .restaurant-name {
                font-size: 36px;
                border-bottom: 2px solid #3498db;
                padding-bottom: 10px;
                margin-bottom: 15px;
              }
              
              .qr-title {
                font-size: 48px;
                margin-bottom: 20px;
              }
              
              .qr-code-wrapper {
                min-height: 500px;
              }
              
              .qr-image {
                max-width: min(85vw, 85vh);
                max-height: min(85vw, 85vh);
                border: 3px solid #ccc;
                padding: 20px;
              }
              
              .qr-instructions {
                font-size: 18px;
                margin-top: 20px;
              }
            }
          </style>
        </head>
        <body>
          ${qrCodesHTML}
          <script>
            window.onload = function() {
              setTimeout(() => {
                window.print();
                window.close();
              }, 1000);
            };
          </script>
        </body>
      </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
    
    toast.success(`Print dialog opened for ${tables.length} QR codes`);
  };

  const toggleTableStatus = async (tableId, newStatus) => {
    try {
      await api.put(`/vendor/tables/${tableId}/toggle-status`, { status: newStatus });
      toast.success(`Table ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
      fetchTables();
    } catch (error) {
      toast.error('Failed to update table status');
    }
  };

  const deleteTable = async (tableId, tableNumber) => {
    if (!window.confirm(`Are you sure you want to delete Table ${tableNumber}?`)) {
      return;
    }
    try {
      await api.delete(`/vendor/tables/${tableId}`);
      toast.success('Table deleted successfully');
      fetchTables();
    } catch (error) {
      toast.error('Failed to delete table');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 ">
      {/* Page Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-1 sm:mb-2">QR Code Management</h1>
            <p className="text-gray-600 text-sm sm:text-base">Generate and manage table QR codes</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <button 
              className="px-3 sm:px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center gap-2 text-sm sm:text-base"
              onClick={downloadAllQRCodes}
            >
              <FaDownload className="text-sm sm:text-base" />
              <span className="hidden sm:inline">Download All</span>
              <span className="sm:hidden">Download</span>
            </button>
            <button 
              className="px-3 sm:px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors duration-200 flex items-center justify-center gap-2 text-sm sm:text-base"
              onClick={printAllQRCodes}
            >
              <FaPrint className="text-sm sm:text-base" />
              <span className="hidden sm:inline">Print All</span>
              <span className="sm:hidden">Print</span>
            </button>
            <button 
              className="px-3 sm:px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold rounded-lg hover:shadow-lg transition-all flex items-center justify-center gap-2 text-sm sm:text-base"
              onClick={() => setShowModal(true)}
            >
              <FaPlus className="text-sm sm:text-base" />
              <span className="hidden sm:inline">Generate QR</span>
              <span className="sm:hidden">Generate</span>
            </button>
          </div>
        </div>
      </div>

      {/* QR Codes Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
        {tables.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((table) => (
          <div key={table._id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 hover:shadow-lg transition-shadow duration-300">
            <div className="text-center">
              <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3 sm:mb-4">Table {table.tableNumber}</h3>
              <div className="relative inline-block mb-3 sm:mb-4">
                <img 
                  src={table.qrCode} 
                  alt={`Table ${table.tableNumber} QR`}
                  className="w-24 h-24 sm:w-32 sm:h-32 object-contain border-2 border-gray-200 rounded-lg"
                />
                <div className="absolute -top-2 -right-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    table.isActive 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {table.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
              
              {/* Assigned Captains */}
              {Array.isArray(table.assignedCaptain) && table.assignedCaptain.length > 0 && (
                <div className="flex flex-wrap gap-1 justify-center mb-3 sm:mb-4">
                  {table.assignedCaptain.slice(0, 2).map((c, idx) => (
                    <span
                      key={c._id}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium"
                    >
                      <FaUserTie size={10} />
                      <span className="hidden sm:inline">{c.name}</span>
                      <span className="sm:hidden">{c.name.split(' ')[0]}</span>
                    </span>
                  ))}
                  {table.assignedCaptain.length > 2 && (
                    <span className="inline-flex items-center px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium">
                      +{table.assignedCaptain.length - 2} more
                    </span>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-center items-center gap-2 sm:gap-3 mt-3 sm:mt-4">
                {/* Assign Captain */}
                <button
                  onClick={() => openAssignModal(table)}
                  title="Assign Captain"
                  className="p-1.5 sm:p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-all duration-200 hover:scale-110"
                >
                  <FaUserTie size={14} className="sm:size-18" />
                </button>

                {/* Download QR */}
                <button
                  onClick={() => downloadQRCode(table)}
                  title="Download QR Code"
                  className="p-1.5 sm:p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200 hover:scale-110"
                >
                  <FaDownload size={14} className="sm:size-18" />
                </button>

                {/* Print QR */}
                <button
                  onClick={() => printQRCode(table)}
                  title="Print QR Code"
                  className="p-1.5 sm:p-2 text-green-600 hover:bg-green-50 rounded-lg transition-all duration-200 hover:scale-110"
                >
                  <FaPrint size={14} className="sm:size-18" />
                </button>

                {/* Delete */}
                <button
                  onClick={() => deleteTable(table._id, table.tableNumber)}
                  title="Delete Table"
                  className="p-1.5 sm:p-2 text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200 hover:scale-110"
                >
                  <FaTrashAlt size={14} className="sm:size-18" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {tables.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-12 text-center">
          <div className="max-w-md mx-auto">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaDownload className="text-gray-400 text-2xl sm:text-3xl" />
            </div>
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">No QR Codes Generated Yet</h3>
            <p className="text-gray-600 text-sm sm:text-base mb-6">Start generating QR codes for your tables to enable customers to scan and order.</p>
            <button 
              className="px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold rounded-lg hover:shadow-lg transition-all flex items-center justify-center mx-auto space-x-2 text-sm sm:text-base"
              onClick={() => setShowModal(true)}
            >
              <FaPlus className="text-sm sm:text-base" />
              <span className="hidden sm:inline">Generate Your First QR Code</span>
              <span className="sm:hidden">Generate First QR</span>
            </button>
          </div>
        </div>
      )}

      {/* Pagination */}
      {Math.ceil(tables.length / itemsPerPage) > 1 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 sm:p-4 mt-3">
          <div className="flex flex-col gap-3 sm:gap-4">
            <div className="text-xs sm:text-sm text-gray-600 text-center sm:text-left">
              Showing {Math.min((currentPage - 1) * itemsPerPage + 1, tables.length)}-{Math.min(currentPage * itemsPerPage, tables.length)} of {tables.length} tables
            </div>
            <div className="flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4">
              <div className="flex gap-2 items-center justify-center">
                <button
                  className="px-3 sm:px-4 py-1.5 sm:py-2 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-400 text-gray-700 rounded-lg font-medium transition-colors duration-200 text-xs sm:text-sm"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  <span className="hidden sm:inline">Previous</span>
                  <span className="sm:hidden">‹</span>
                </button>
                {[...Array(Math.ceil(tables.length / itemsPerPage))].map((_, idx) => {
                  const pageNum = idx + 1;
                  const totalQRPages = Math.ceil(tables.length / itemsPerPage);
                  if (
                    pageNum === 1 ||
                    pageNum === totalQRPages ||
                    (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={pageNum}
                        className={`min-w-[28px] sm:min-w-[36px] px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg font-medium transition-all duration-200 text-xs sm:text-sm ${
                          pageNum === currentPage 
                            ? 'bg-blue-500 text-white shadow-lg' 
                            : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                        }`}
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </button>
                    );
                  } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                    return <span key={pageNum} className="px-1 sm:px-2 text-gray-400 text-xs sm:text-sm">...</span>;
                  }
                  return null;
                })}
                <button
                  className="px-3 sm:px-4 py-1.5 sm:py-2 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-400 text-gray-700 rounded-lg font-medium transition-colors duration-200 text-xs sm:text-sm"
                  onClick={() => setCurrentPage(prev => Math.min(Math.ceil(tables.length / itemsPerPage), prev + 1))}
                  disabled={currentPage === Math.ceil(tables.length / itemsPerPage)}
                >
                  <span className="hidden sm:inline">Next</span>
                  <span className="sm:hidden">›</span>
                </button>
              </div>
              <div className="flex items-center gap-2 text-xs sm:text-sm justify-center">
                <span className="text-gray-600">Show</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="px-2 sm:px-3 py-1 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors duration-200 text-xs sm:text-sm"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
                <span className="text-gray-600">per page</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Generate QR Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 sm:p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-4 sm:p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-lg sm:text-xl font-bold">Generate QR Codes</h2>
                <button 
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors duration-200"
                  onClick={() => setShowModal(false)}
                >
                  <FaPlus className="text-lg sm:text-xl rotate-45" />
                </button>
              </div>
            </div>
            
            <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
              {/* Generate Multiple Tables */}
              <div className="mb-6 sm:mb-8">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Generate Multiple Tables</h3>
                <form onSubmit={generateMultipleTables} className="space-y-3 sm:space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Number of Tables</label>
                    <input
                      type="text"
                      className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors duration-200 text-sm sm:text-base"
                      value={numberOfTables}
                      inputMode="numeric"
                      placeholder="Number of tables"
                      required
                      onChange={(e) => {
                        let value = e.target.value.replace(/\D/g, ''); // only digits

                        // limit 0–100
                        if (Number(value) > 100) {
                          value = '100';
                        }

                        setNumberOfTables(value === '' ? '' : Number(value));
                      }}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Tables will be numbered starting from {tables.length}
                    </p>
                  </div>
                  <button 
                    type="submit" 
                    className="w-full px-4 py-2.5 sm:py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold rounded-lg hover:shadow-lg transition-all text-sm sm:text-base"
                  >
                    Generate {numberOfTables} QR Code{numberOfTables > 1 ? 's' : ''}
                  </button>
                </form>
              </div>

              <div className="border-t border-gray-200 pt-6 sm:pt-8">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Generate Specific Table</h3>
                <form onSubmit={generateSpecificTable} className="space-y-3 sm:space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Table Number</label>
                      <input
                        type="text"
                        className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors duration-200 text-sm sm:text-base"
                        value={specificTableNumber}
                        inputMode="numeric"
                        placeholder="Enter table number"
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, ''); // only digits
                          setSpecificTableNumber(value);
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Table Name</label>
                      <input
                        type="text"
                        className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors duration-200 text-sm sm:text-base"
                        placeholder="Optional table name"
                        disabled
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">From Table Number</label>
                      <input
                        type="text"
                        className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors duration-200 text-sm sm:text-base"
                        value={fromTableNumber}
                        inputMode="numeric"
                        placeholder="e.g., 101"
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, ''); // only digits
                          setFromTableNumber(value);
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">To Table Number</label>
                      <input
                        type="text"
                        className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors duration-200 text-sm sm:text-base"
                        value={toTableNumber}
                        inputMode="numeric"
                        placeholder="e.g., 110"
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, ''); // only digits
                          setToTableNumber(value);
                        }}
                      />
                    </div>
                  </div>

                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-xs sm:text-sm text-blue-800">
                      <strong>Note:</strong> Either provide a single Table Number or use From/To to generate multiple QR codes at once.
                      If using From/To, QR codes will be created sequentially (e.g., 101 to 110 creates QR codes for tables 101, 102, 103...110).
                    </p>
                  </div>

                  <button 
                    type="submit" 
                    className="w-full px-4 py-2.5 sm:py-3 bg-gray-600 hover:bg-gray-700 text-white font-semibold rounded-lg transition-colors duration-200 text-sm sm:text-base"
                  >
                    Generate Specific QR Code{fromTableNumber && toTableNumber ? 's' : ''}
                  </button>
                </form>
              </div>
            </div>

            <div className="bg-gray-50 px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-200">
              <button 
                className="w-full px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors duration-200 text-sm sm:text-base"
                onClick={() => setShowModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Captain Modal */}
      {showAssignModal && selectedTable && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 sm:p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white p-4 sm:p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-lg sm:text-xl font-bold">Assign Captain - Table {selectedTable.tableNumber}</h2>
                <button 
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors duration-200"
                  onClick={() => setShowAssignModal(false)}
                >
                  <FaPlus className="text-lg sm:text-xl rotate-45" />
                </button>
              </div>
            </div>
            
            <div className="p-4 sm:p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Captains</label>
                <select
                  multiple
                  className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none transition-colors duration-200 h-32 text-sm sm:text-base"
                  value={selectedCaptainIds}
                  onChange={(e) =>
                    setSelectedCaptainIds(
                      Array.from(e.target.selectedOptions, o => o.value)
                    )
                  }
                >
                  {captains.map(c => (
                    <option key={c._id} value={c._id} className="py-2">
                      {c.name} ({c.username})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">Hold Ctrl / Cmd to select multiple captains</p>
              </div>
            </div>

            <div className="bg-gray-50 px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-200 flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
              <button 
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors duration-200 text-sm sm:text-base"
                onClick={() => setShowAssignModal(false)}
              >
                Cancel
              </button>
              <button 
                className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-medium transition-colors duration-200 text-sm sm:text-base"
                onClick={assignCaptain}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QRCodeManagement;
